import {apiFetch, logRequest, logResponse, logError} from 'src/app/api/ApiCore';
import {isDev} from 'src/app/constant/Constants';

const OPERATION = {
    GET_REASON_CODES: 'GetCategoryAndReasonCodeMappings',
    SUBMIT_EXEMPTIONS: 'SubmitExemptionRequest',
    GET_STANDING: 'GetStanding',
    CREATE_SLP_TICKET: 'CreateSlpTicket',
    ADD_COMMENT: 'AddComment',
} as const;

export interface ScoreView {
    scoreType: string;
    value: number;
    issueRecency: string;
    standingBucket?: string;
}

export interface GetStandingResponsePayload {
    overallStanding: string;
    standingBucketPosition: number;
    scores: ScoreView[];
    hasActiveOffboarding: boolean;
    dataFreshnessDate: string;
    /**
     * The snapshot's own creation time, from FlexCyberno's createdDate. This is the auditors' source
     * of truth for which snapshot they are looking at; dataFreshnessDate advances on every recompute
     * by design, so it cannot serve that purpose.
     *
     * Optional: absent on a DEGRADED panel, and absent until the service-side change ships, so the
     * UI must render a fallback rather than assume it is present.
     */
    createdDate?: string;
    panelStatus: 'OK' | 'DEGRADED';
}

export interface GetStandingResponse {
    result: string;
    msg: string;
    data: GetStandingResponsePayload;
}

export namespace OdqAndReliabilityApi {

    /**
     * Fetches reason code mappings for ODQ/Reliability exemptions.
     * GET /odq/reason-codes?country=X&opportunityType=Y
     */
    export const getCategoryAndReasonCodeMappings = async (country: string, opportunityType: string): Promise<any> => {
        if (isDev()) {
            const {MOCK_REASON_CODES} = await import('src/app/mock/MockReviewData');
            return {result: 'SUCCESS', data: {mappings: MOCK_REASON_CODES}};
        }
        const startTime = Date.now();
        logRequest(OPERATION.GET_REASON_CODES, {country, opportunityType});
        try {
            const params = new URLSearchParams({country});
            if (opportunityType) params.append('opportunityType', opportunityType);
            const response = await apiFetch('GET', `/odq/reason-codes?${params.toString()}`);
            logResponse(OPERATION.GET_REASON_CODES, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.GET_REASON_CODES, error, {country, opportunityType});
            throw error;
        }
    };

    /**
     * One defect the auditor asked to exempt. `defectType` is what makes the item addressable: one
     * opportunity commonly carries several defect types, so opportunityId alone cannot say which row
     * was selected.
     */
    export interface ExemptionRequestItem {
        opportunityId: string;
        opportunityType: string;
        defectType: string;
    }

    /**
     * Submits exemption requests for ODQ/Reliability defects.
     * POST /odq/cases/{caseId}/exemptions
     *
     * One `category`/`reason` applies to every item in the batch.
     */
    export const submitExemptions = async (
        caseId: string,
        transporterId: string,
        category: string,
        reason: string,
        ticketId: string,
        exemptionRequests: ExemptionRequestItem[]
    ): Promise<any> => {
        if (isDev()) {
            return {
                result: 'success',
                data: {
                    results: exemptionRequests.map(e => ({
                        opportunityId: e.opportunityId,
                        defectType: e.defectType,
                        category,
                        reason,
                        requestId: `odq-mock-${Date.now()}`,
                        outcome: 'ACCEPTED',
                        exemptionStatus: 'ACCEPTED',
                        submissionResult: 'REQUEST_CREATED',
                        message: null
                    })),
                    andonCordActive: false
                }
            };
        }
        const startTime = Date.now();
        logRequest(OPERATION.SUBMIT_EXEMPTIONS, {caseId, transporterId, category, count: exemptionRequests.length});
        try {
            const response = await apiFetch('POST', `/odq/cases/${encodeURIComponent(caseId)}/exemptions`, {
                transporterId,
                category,
                reason,
                ticketId,
                exemptionRequests
            });
            logResponse(OPERATION.SUBMIT_EXEMPTIONS, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.SUBMIT_EXEMPTIONS, error, {caseId});
            throw error;
        }
    };

    /**
     * Fetches the DP's current standing tier, score breakdown, and offboarding status.
     * GET /odq/providers/{providerId}/standing?beforeDate=<optional>
     */
    export const getStanding = async (providerId: string, opportunityType?: string, beforeDate?: string): Promise<GetStandingResponse> => {
        if (isDev()) {
            const {MOCK_ODQ_STANDING_RESPONSE, MOCK_RELIABILITY_STANDING_RESPONSE} = await import('src/app/mock/MockReviewData');
            return opportunityType === 'RESERVATION' ? MOCK_RELIABILITY_STANDING_RESPONSE : MOCK_ODQ_STANDING_RESPONSE;
        }
        const startTime = Date.now();
        logRequest(OPERATION.GET_STANDING, {providerId, opportunityType, beforeDate});
        try {
            const params = new URLSearchParams();
            if (opportunityType) params.append('opportunityType', opportunityType);
            if (beforeDate) params.append('beforeDate', beforeDate);
            const standingQuery = params.toString() ? `?${params.toString()}` : '';
            const response = await apiFetch('GET', `/odq/providers/${encodeURIComponent(providerId)}/standing${standingQuery}`);
            logResponse(OPERATION.GET_STANDING, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.GET_STANDING, error, {providerId});
            throw error;
        }
    };

    export interface CreateSlpTicketPayload {
        status: 'CREATED' | 'FAILED';
        ticketId?: string;
        ticketLink?: string;
        failureReason?: string;
    }

    export interface CreateSlpTicketResponse {
        result: string;
        msg: string;
        data: CreateSlpTicketPayload;
    }

    /**
     * Creates an SLP escalation ticket for ODQ cases.
     * POST /odq/cases/{caseId}/createtickettoslp
     */
    export const createSlpTicket = async (caseId: string, request: {
        stationCode: string;
        transporterId: string;
        defectDate: string;
        trackingIds: string[];
        cti: {category: string; type: string; item: string};
        idempotencyKey?: string;
    }): Promise<CreateSlpTicketResponse> => {
        if (isDev()) {
            const {MOCK_SLP_TICKET_RESPONSE} = await import('src/app/mock/MockReviewData');
            return MOCK_SLP_TICKET_RESPONSE;
        }
        const startTime = Date.now();
        logRequest(OPERATION.CREATE_SLP_TICKET, {caseId, ...request});
        try {
            const response = await apiFetch('POST', `/odq/cases/${encodeURIComponent(caseId)}/createtickettoslp`, request);
            logResponse(OPERATION.CREATE_SLP_TICKET, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.CREATE_SLP_TICKET, error, {caseId});
            throw error;
        }
    };

    /**
     * Adds a comment to the review's case history (audit trail).
     * POST /lighthouse/reviews/{reviewId}/comment
     */
    export const addComment = async (reviewId: string, comment: string): Promise<any> => {
        if (isDev()) {
            console.log('[AddComment Mock]', {reviewId, comment});
            return {result: 'success', msg: 'Comment added successfully'};
        }
        const startTime = Date.now();
        try {
            logRequest(OPERATION.ADD_COMMENT, {reviewId});
            const response = await apiFetch('POST', `/lighthouse/reviews/${encodeURIComponent(reviewId)}/comment`, {comment});
            logResponse(OPERATION.ADD_COMMENT, Date.now() - startTime);
            return response;
        } catch (error) {
            logError(OPERATION.ADD_COMMENT, error, {reviewId});
            console.error('[AddComment] Failed to add comment', error);
        }
    };
}
